import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const serviceTypeOptions = [
  { value: "customer_indexing", label: "Customer Registration" },
  { value: "customer_profiling", label: "Customer Profiling & Analysis" },
  { value: "meter_registration", label: "Meter Registration" },
  { value: "meter_installation", label: "Meter Installation" },
  { value: "meter_un_installation", label: "Meter Un Installation" },
  { value: "disconnection", label: "Service Disconnection" },
  { value: "inspection", label: "Routine Inspection" },
  { value: "maintenance", label: "Maintenance" },
  { value: "repair", label: "Repair" },
];

interface FormData {
  title: string;
  description: string;
  remarks: string;
  type: string;
  priority: string;
  customerId: string;
  meterId: string;
  scheduledDate: string;
}

interface MeterApiResponse {
  success: boolean;
  data?: Array<{
    id: string;
    meterNumber: string;
  }>;
}

interface HierarchyApiResponse {
  success: boolean;
  data?: {
    customers?: Array<{
      id: string;
      name: string;
      customerCode?: string;
      email?: string;
      phone?: string;
    }>;
  };
}

const CreateServiceOrder: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    remarks: "",
    type: "maintenance",
    priority: "medium",
    customerId: "",
    meterId: "",
    scheduledDate: "",
  });
  const [meterOptions, setMeterOptions] = useState<Array<{ id: string; meterNumber: string }>>([]);
  const [isMetersLoading, setIsMetersLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<
    Array<{ id: string; name: string; customerCode?: string; email?: string; phone?: string }>
  >([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState<boolean>(false);

  const selectedCustomer = useMemo(
    () => customerOptions.find((customer) => customer.id === formData.customerId),
    [customerOptions, formData.customerId]
  );

  const isOptionsLoading = isMetersLoading || isCustomersLoading;

  const refreshReferenceLists = useCallback(async (): Promise<void> => {
    setIsMetersLoading(true);
    setIsCustomersLoading(true);
    try {
      const [metersResponse, hierarchyResponse] = await Promise.all([
        fetch("https://backend.voltreum.com/api/v2/meters"),
        fetch("https://backend.voltreum.com/api/v2/hierarchy"),
      ]);

      try {
        const result: MeterApiResponse = await metersResponse.json();
        const meters = Array.isArray(result?.data) ? result.data : [];
        setMeterOptions(
          meters
            .filter((meter) => meter.id && meter.meterNumber)
            .map((meter) => ({ id: meter.id, meterNumber: meter.meterNumber }))
        );
      } catch (error) {
        console.error("Failed to fetch meter options:", error);
        setMeterOptions([]);
      }

      try {
        const result: HierarchyApiResponse = await hierarchyResponse.json();
        const customers = Array.isArray(result?.data?.customers)
          ? (result.data?.customers ?? [])
          : [];
        setCustomerOptions(
          customers
            .filter((customer) => customer.id && customer.name)
            .map((customer) => ({
              id: customer.id,
              name: customer.name,
              customerCode: customer.customerCode,
              email: customer.email,
              phone: customer.phone,
            }))
        );
      } catch (error) {
        console.error("Failed to fetch customer options:", error);
        setCustomerOptions([]);
      }
    } catch (error) {
      console.error("Failed to load reference lists:", error);
      setMeterOptions([]);
      setCustomerOptions([]);
    } finally {
      setIsMetersLoading(false);
      setIsCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshReferenceLists();
  }, [refreshReferenceLists]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Creating service order:", {
      ...formData,
      customerInfo: selectedCustomer || null,
      createdAt: new Date().toISOString(),
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      remarks: "",
      type: "maintenance",
      priority: "medium",
      customerId: "",
      meterId: "",
      scheduledDate: "",
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Create New Service Order</CardTitle>
          <CardDescription>
            Create a comprehensive service request for a customer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, type: value }))
                  }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, priority: value }))
                  }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="customerId">Customer</Label>
                <Select
                  value={formData.customerId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      customerId: value,
                    }))
                  }>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={isCustomersLoading ? "Loading customers..." : "Select Customer"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {customerOptions.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="meterId">Meter</Label>
                <Select
                  value={formData.meterId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, meterId: value }))
                  }>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={isMetersLoading ? "Loading meters..." : "Select Meter"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {meterOptions.map((meter) => (
                      <SelectItem key={meter.id} value={meter.meterNumber}>
                        {meter.meterNumber}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="scheduledDate">Scheduled Date</Label>
                <Input
                  id="scheduledDate"
                  type="datetime-local"
                  value={formData.scheduledDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData((prev) => ({
                      ...prev,
                      scheduledDate: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="remarks">Remarks</Label>
                <Textarea
                  id="remarks"
                  value={formData.remarks}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData((prev) => ({ ...prev, remarks: e.target.value }))
                  }
                  rows={2}
                />
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Create
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateServiceOrder;
